import {FormikErrors} from 'formik';
import {set} from 'lodash-es';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {builderModifyChanges} from '../BuilderModify/BuilderModify';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import validateUUID from '../../../utils/validateUUID';
import {
  getPotContactId,
  updateRecsVarsProps,
  upsertBuilderHeader,
} from './builderHelpers';
import {getPlanFeesServerFromFormik} from '../CreateBuilder/BuilderPlanFees';
import {SaltusAny} from '../../../utils/SaltusAny';

function modifyFieldsToBuilderNote(fields: builderFieldsType['modify']): string {
  const changes: string[] = [];
  if (fields?.feeChange) {
    changes.push(builderModifyChanges.feeChange);
  }
  if (fields?.mandateChange) {
    changes.push(builderModifyChanges?.mandateChange);
  }
  if (fields?.regularContributionChange) {
    changes.push(builderModifyChanges?.regularContributionChange);
  }
  if (fields?.regularWithdrawalChange) {
    changes.push(builderModifyChanges?.regularWithdrawalChange);
  }
  if (fields?.changeOfOwnership) {
    changes.push(builderModifyChanges?.changeOfOwnership);
  }
  changes.push(fields?.note || '');
  return changes.join('\n');
}

export const modifyValidator = (
  values: builderFieldsType,
  errors: SaltusAny
): FormikErrors<builderFieldsType> => {
  if (!values.goal?.id) {
    if (!values.isSppDa) {
      errors.goal = 'Please select a goal';
    }
  }
  const sourceID = values.modify?.source?.id;
  if (!sourceID) {
    set(errors, 'modify.source', 'Select product type');
  }
  if (
    !values.modify?.feeChange &&
    !values.modify?.mandateChange &&
    !values.modify?.regularContributionChange &&
    !values.modify?.regularWithdrawalChange &&
    !values.modify?.changeOfOwnership
  ) {
    set(errors, 'modify.action', 'At least one must be selected');
  }
  if (!values.modify?.note) {
    if (values.modify?.mandateChange) {
      // ignore
    } else {
      set(errors, 'modify.note', 'A note is required');
    }
  }
  if (values.modify?.mandateChange) {
    if (!values.modify.model) {
      set(errors, 'modify.model', 'A mandate is required');
    }
  }
  if (
    (values.modify?.feeChange || values.modify?.mandateChange) &&
    values.mainPlanType === 'pension' &&
    !values.modify.source.isChild &&
    values.modify.source.isSaltus
  ) {
    set(
      errors,
      'modify.source',
      "If it's a Saltus SIPP, fees and mandates will always go against the GIA gross"
    );
  }
  return errors;
};

export const saveModify = async (props: updateRecsVarsProps): Promise<string> => {
  const {accountId, values} = props;
  const {contact, urgency, urgencyReason, modify} = values;

  const ovrecommendId = await upsertBuilderHeader(props, {
    action: OVRecommendActionEnum.MODIFY,
    accountId: validateUUID(accountId),
    goalId: validateUUID(values.goal?.id),
    contactId: getPotContactId(contact),
    urgency,
    urgencyReason,
    note: modifyFieldsToBuilderNote(modify),
  });

  const potId = modify?.source.parentPotId ?? modify?.source.id;

  await props.upsertBuilderModify({
    variables: {
      ovrecommendId: validateUUID(ovrecommendId)!,
      potId: validateUUID(potId)!,
      feeIds: modify?.planFees ? getPlanFeesServerFromFormik(modify.planFees) : undefined,
      modelPortfolioId:
        typeof modify?.model !== 'string' ? validateUUID(modify?.model?.id) : undefined,
      nonSaltusMandate: typeof modify?.model === 'string' ? modify.model : undefined,
    },
  });

  return ovrecommendId;
};
